import { AlertTriangle, CalendarClock, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { useT } from '@/core/providers/I18nProvider';

import {
  isAnnounced,
  localizeIncidentText,
  selectVisibleIncident,
  type IncidentSeverity,
} from './maintenance-incident';
import { useMaintenanceIncidents } from './useMaintenanceIncidents';

import type { ReactElement } from 'react';

/**
 * Global in-app maintenance / incident banner (D30). Reads the public status
 * feed (out-of-band from the api — see {@link useMaintenanceIncidents}) and, when
 * an incident is active or imminently announced, surfaces a persistent,
 * dismissible alert across every dashboard page — the in-product half of the
 * support-SLA "trust page AND in-app banner" promise.
 *
 * Renders nothing in the common case (no incidents). Dismissal is per-browser +
 * per-incident-id (localStorage), so dismissing one announcement never hides a
 * later, distinct incident. RTL-correct via logical spacing + `<bdi>` isolation
 * of operator-authored text; `role="alert"` announces it once on appearance.
 *
 * Sibling of {@link PaymentAlertBanner} — same global-banner shape, mounted in
 * the AppShell chrome.
 */

const DISMISS_STORAGE_KEY = 'syncanix:maintenance-dismissed';

const SEVERITY_STYLES: Record<IncidentSeverity, string> = {
  maintenance: 'border-info/40 bg-info/10 text-info-foreground',
  degraded: 'border-warning/40 bg-warning/10 text-warning-foreground',
  outage: 'border-danger/40 bg-danger/10 text-danger-foreground',
};

function readDismissed(): ReadonlySet<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((v): v is string => typeof v === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function persistDismissed(ids: ReadonlySet<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // localStorage unavailable (private mode / quota) — dismissal stays
    // in-memory for this session only; never throw into the chrome.
  }
}

export function MaintenanceBanner(): ReactElement | null {
  const { t, locale } = useT();
  const incidents = useMaintenanceIncidents();
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => readDismissed());

  const incident = useMemo(
    () => selectVisibleIncident(incidents, Date.now(), dismissed),
    [incidents, dismissed],
  );

  const handleDismiss = useCallback(() => {
    if (incident === undefined) return;
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(incident.id);
      persistDismissed(next);
      return next;
    });
  }, [incident]);

  if (incident === undefined) return null;

  const title = localizeIncidentText(incident.title, locale) ?? incident.id;
  const body = localizeIncidentText(incident.body, locale);
  const Icon = isAnnounced(incident, Date.now()) ? CalendarClock : AlertTriangle;

  return (
    <div
      role="alert"
      className={`flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b px-6 py-3 text-sm ${SEVERITY_STYLES[incident.severity]}`}
    >
      <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
      <div className="flex-1">
        <span className="font-medium">{t(`maintenanceBanner.severity.${incident.severity}`)}</span>{' '}
        <span>
          <bdi>{title}</bdi>
        </span>
        {body !== undefined && (
          <>
            {' '}
            <span className="opacity-90">
              <bdi>{body}</bdi>
            </span>
          </>
        )}
      </div>
      <button
        type="button"
        aria-label={t('maintenanceBanner.dismiss')}
        onClick={handleDismiss}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-foreground/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}
